import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/liquicent-logo.png";

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/users/request-password-reset`, { email });
      setSent(true);
      toast.success('Password reset instructions sent to your email');
    } catch (error) {
      toast.error('Error sending reset email');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] pt-32 pb-20 px-6">
        <Helmet>
          <title>Check Your Email | Liquicent</title>
        </Helmet>
        
        <div className="max-w-md mx-auto">
          <Card 
            className="p-8 bg-white border border-[#E5E7EB] rounded-2xl text-center"
            style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
          >
            <div className="w-16 h-16 bg-[#2CB1A1]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-[#2CB1A1]" />
            </div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Check Your Email</h2>
            <p className="text-[#64748B] mb-6">
              We've sent password reset instructions to <strong className="text-[#0F172A]">{email}</strong>
            </p>
            <p className="text-sm text-[#94A3B8] mb-8">
              Didn't receive the email? Check your spam folder or try again.
            </p>
            <Link to="/user/login">
              <Button className="bg-[#1A3A8F] hover:bg-[#142C6D] text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F7FB] pt-32 pb-20 px-6">
      <Helmet>
        <title>Forgot Password | Liquicent</title>
      </Helmet>
      
      <div className="max-w-md mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-6">
            <img 
              src={LOGO_URL} 
              alt="Liquicent" 
              className="w-12 h-12 object-contain"
            />
            <span className="text-2xl font-semibold text-[#0F172A]">Liquicent</span>
          </Link>
          <h1 className="text-3xl font-bold text-[#0F172A] mb-2">Forgot password?</h1>
          <p className="text-[#64748B]">Enter your email to receive reset instructions</p>
        </div>

        <Card 
          className="p-8 bg-white border border-[#E5E7EB] rounded-2xl"
          style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#0F172A] text-sm font-medium">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#94A3B8] w-5 h-5" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-[#F6F7FB] border-[#E5E7EB] text-[#0F172A] focus:border-[#1A3A8F]"
                  placeholder="john@company.com"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#1A3A8F] hover:bg-[#142C6D] text-white py-6 text-base font-medium rounded-lg transition-all"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5 mr-2" />
                  Send Reset Link
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#E5E7EB] text-center">
            <Link 
              to="/user/login" 
              className="inline-flex items-center text-[#64748B] hover:text-[#1A3A8F] text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Login
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
